import React, { useMemo, useRef, useState } from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import api from '@/services/api.service';
import {
  Alert,
  Card,
  Descriptions,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Divider,
  Statistic,
  Spin,
  message,
  Popconfirm,
  Modal,
  Tooltip,
  Select,
  Radio,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  SendOutlined,
  FileTextOutlined,
  PrinterOutlined,
  CloseCircleOutlined,
  ShoppingCartOutlined,
  SwapOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useQuotation, useApproveQuotation, useSendQuotation, useDeleteQuotation, useConvertQuotationToSalesOrder, useAcceptQuotation, useCreateQuotationRevision, QUOTATION_KEYS } from '../../hooks/useQuotation';
import { useSalesOrder } from '../../hooks/useSalesOrder';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { canDeleteQuotation, canEditQuotation } from '../../utils/documentDeletePolicy';
import {
  canCreateQuotationRevision,
  displayQuotationRevisionLabel,
  formatQuotationPrintIdentity,
} from '../../utils/quotationRevisionPolicy';
import { CreateQuotationRevisionModal } from '@/components/crm/CreateQuotationRevisionModal';
import { QuotationNegotiationWorkspace } from '@/components/crm/quotation-workspace';
import { useQuotationWorkspaceData } from '@/hooks/useQuotationWorkspaceData';
import type { QuotationPrimaryCtaKey } from '@/utils/quotationPrimaryCta';
import { useSystemSettings } from '@/hooks/useSettings';
import { useQuotationPrintCoverLetter } from '@/hooks/useQuotationDefaults';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  QuotationItem,
  type CustomerOverheadPrintOption,
  type QuotationPrintMode,
} from '@/services/quotation.service';
import { useAuthStore } from '@/store/authStore';
import { quotationConsolidationService } from '@/services/quotationConsolidation.service';
import { PrintableQuotation } from '@/components/quotation/PrintableQuotation';
import { getReportDocumentFile } from '@/services/reportDocuments.service';
import { openBlobInNewTab } from '@/reports/utils/exportTable';
import { installSalesQuotationPrintPageMargins } from '@/utils/salesQuotationPrintPage';
import ReactDOM from 'react-dom/client';
import DocumentList from '../../components/documents/DocumentList';
import DocumentUpload from '../../components/documents/DocumentUpload';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { PendingApproverText } from '@/components/approval/PendingApproverText';
import { ENTITY_TYPE_QUOTATION } from '@/constants/approval-entity-types';
import { SalesTeamTransferModal } from '@/components/sales/SalesTeamTransferModal';
import { TransferHistoryPanel } from '@/components/sales/TransferHistoryPanel';
import quotationService from '@/services/quotation.service';
import { approvalService } from '@/services/approval.service';
import contractReviewService from '@/services/contractReview.service';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import {
  formatRentalCharges,
  formatRentalPeriod,
  formatRentalQuantity,
  hasAnyRentalQuotationLine,
  isRentalQuotationItem,
  rentalLineRate,
} from '@/utils/quotationRentalDisplay';

const { Title, Text } = Typography;

const QuotationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { baseCurrency } = useCurrency();
  const printCoverLetter = useQuotationPrintCoverLetter();
  const queryClient = useQueryClient();
  const [qtSubmitLoading, setQtSubmitLoading] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [reviseOpen, setReviseOpen] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [acceptConfirmOpen, setAcceptConfirmOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [convertDeptModalOpen, setConvertDeptModalOpen] = useState(false);
  const [convertDepartmentId, setConvertDepartmentId] = useState<string | undefined>();
  const [printMode, setPrintMode] = useState<QuotationPrintMode>('customer');
  const [customerOverheadDisplay, setCustomerOverheadDisplay] = useState<CustomerOverheadPrintOption>('none');
  const { user } = useAuthStore();
  const perms = user?.permissions || [];
  const canViewProfitability =
    Boolean(user?.is_super_admin) ||
    perms.includes('growth.profitability.view') ||
    perms.includes('growth.margin.view') ||
    perms.includes('growth.view');

  const { data: quotationData, isLoading } = useQuotation(id!);
  const { data: linkedSalesOrder } = useSalesOrder(quotationData?.sales_order_id);
  const { data: settings } = useSystemSettings();
  const approveQuotationMutation = useApproveQuotation();
  const sendQuotationMutation = useSendQuotation();
  const deleteQuotationMutation = useDeleteQuotation();
  const convertToSalesOrderMutation = useConvertQuotationToSalesOrder();

  const { data: contractReviews = [], refetch: refetchContractReviews } = useQuery({
    queryKey: ['contract-reviews-by-quotation', id],
    queryFn: () => contractReviewService.listByQuotation(id!),
    enabled: !!id,
  });

  const { data: linkedSalesOrders = [] } = useQuery({
    queryKey: ['quotation-so-links', id],
    queryFn: () => quotationConsolidationService.getLinksByQuotation(id!),
    enabled: !!id,
    staleTime: 60000,
  });

  const { data: estimationFamily = [] } = useQuery({
    queryKey: ['crm-estimation-revision-family', quotationData?.estimation_id],
    enabled: !!quotationData?.estimation_id,
    queryFn: async () => {
      const res = await api.get(
        `/crm/estimations/${quotationData!.estimation_id}/revision-family`
      );
      return (res.data?.data ?? res.data) as Array<{
        id: string;
        revision_number: number;
        status: string;
        estimation_number: string;
        total_cost: number;
      }>;
    },
  });

  const approvedContractReview = useMemo(() => {
    return contractReviews.find(
      (r) =>
        r.review_status === 'Approved' &&
        !r.is_stale &&
        (r.final_recommendation !== 'approved_with_conditions' || !!r.conditions_acknowledged_at)
    );
  }, [contractReviews]);
  const activeContractReview = useMemo(
    () =>
      contractReviews.find(
        (r) =>
          r.review_status !== 'Rejected' &&
          r.review_status !== 'Cancelled' &&
          !r.is_stale
      ),
    [contractReviews]
  );
  const staleContractReview = useMemo(
    () => contractReviews.find((r) => r.is_stale && r.review_status !== 'Cancelled'),
    [contractReviews]
  );
  const activeReviewHint = 'Active review already exists; open or cancel existing first.';

  const createContractReviewMutation = useMutation({
    mutationFn: () => contractReviewService.createFromQuotation(id!),
    onSuccess: (cr) => {
      message.success(`Contract Review ${cr.review_number} created`);
      refetchContractReviews();
      navigate(`/growth/contract-reviews/${cr.id}`);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Failed to create review'),
  });
  const acceptQuotationMutation = useAcceptQuotation();

  const quotation = quotationData;
  const workspace = useQuotationWorkspaceData(quotation);
  const quotationDeleteCheck = useMemo(
    () => (quotation ? canDeleteQuotation(quotation) : { ok: false }),
    [quotation]
  );
  const quotationEditCheck = useMemo(
    () => (quotation ? canEditQuotation(quotation) : { ok: false }),
    [quotation]
  );
  const quotationReviseCheck = useMemo(
    () => (quotation ? canCreateQuotationRevision(quotation) : { ok: false }),
    [quotation]
  );
  const createRevisionMutation = useCreateQuotationRevision();
  const isTradingRental = !!quotation?.trading_rental_estimation_id;
  const showRentalLineColumns =
    isTradingRental || hasAnyRentalQuotationLine(quotation?.items);
  const canConvertWithContractReview = isTradingRental || !!approvedContractReview;

  const breadcrumbSegments = useMemo(
    () =>
      quotation
        ? [
            { title: 'Growth & Revenue', path: '/growth/dashboard' },
            { title: 'Quotations', path: '/growth/quotations' },
            { title: quotation.quotation_number || 'Quotation' },
            { title: displayQuotationRevisionLabel(quotation.revision_number) },
          ]
        : null,
    [quotation?.id, quotation?.quotation_number, quotation?.revision_number]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const tradingRentalEstimationLabel =
    quotation?.trading_rental_estimation_summary?.estimation_number?.trim() ||
    (quotation?.trading_rental_estimation_id ? 'Open estimation' : null);
  const boqEstimationLabel =
    quotation?.boq_summary?.boq_number?.trim() ||
    (quotation?.boq_id ? 'Open BOQ estimation' : null);
  const multiItemEstimationLabel =
    quotation?.estimation_summary?.estimation_number?.trim() ||
    (quotation?.estimation_id ? 'Open estimation' : null);
  const hasSourceEstimation =
    !!(
      quotation?.boq_id?.trim() ||
      quotation?.estimation_id?.trim() ||
      quotation?.trading_rental_estimation_id?.trim()
    );

  const syncableSource =
    !!(quotation?.boq_id?.trim() || quotation?.trading_rental_estimation_id?.trim()) &&
    quotation?.is_latest_revision !== false &&
    !quotation?.is_converted_to_sales_order;
  const { data: sourceSync } = useQuery({
    queryKey: ['quotation-source-sync', quotation?.id, 'banner', quotation?.updated_at],
    queryFn: () => quotationService.getSourceSync(quotation!.id),
    enabled: !!quotation?.id && syncableSource,
    retry: false,
    staleTime: 60_000,
  });
  const sourceSyncCommercial = sourceSync?.summary.commercial_changes ?? 0;
  const sourceSyncHousekeeping = (sourceSync?.summary.cost_only ?? 0) + (sourceSync?.summary.relinked ?? 0);

  const estimateLabel = useMemo(() => {
    if (boqEstimationLabel) return `BOQ ${boqEstimationLabel}`;
    if (multiItemEstimationLabel) return multiItemEstimationLabel;
    if (tradingRentalEstimationLabel) return tradingRentalEstimationLabel;
    return null;
  }, [boqEstimationLabel, multiItemEstimationLabel, tradingRentalEstimationLabel]);

  const openEstimate = () => {
    if (!quotation) return;
    if (quotation.boq_id?.trim()) {
      navigate(`/growth/estimations/boq/edit/${quotation.boq_id}`);
    } else if (quotation.estimation_id?.trim()) {
      navigate(`/growth/estimations/multi-item/${quotation.estimation_id}`);
    } else if (quotation.trading_rental_estimation_id?.trim()) {
      navigate(`/growth/estimations/trading-rental/${quotation.trading_rental_estimation_id}`);
    }
  };

  const priorOfferRevision = useMemo(() => {
    const prior = workspace.currentOffer;
    if (!prior || prior.id === quotation?.id) return null;
    return prior.revision_number;
  }, [workspace.currentOffer, quotation?.id]);

  const linkedEstimation = useMemo(
    () => estimationFamily.find((e) => e.id === quotation?.estimation_id),
    [estimationFamily, quotation?.estimation_id]
  );

  const latestApprovedEstimate = useMemo(
    () =>
      [...estimationFamily]
        .filter((e) => e.status === 'Approved')
        .sort((a, b) => b.revision_number - a.revision_number)[0],
    [estimationFamily]
  );

  const estimateCostDelta = useMemo(() => {
    if (!linkedEstimation || !latestApprovedEstimate) return null;
    return Number(latestApprovedEstimate.total_cost) - Number(linkedEstimation.total_cost);
  }, [linkedEstimation, latestApprovedEstimate]);

  const handlePrimaryCta = async (key: QuotationPrimaryCtaKey) => {
    if (!quotation) return;
    switch (key) {
      case 'submit_approval':
        setQtSubmitLoading(true);
        try {
          await quotationService.submitForUniversalApproval(id!);
          message.success('Submitted for approval');
          queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(id!) });
        } catch (e: unknown) {
          const err = e as { response?: { data?: { message?: string } }; message?: string };
          message.error(err?.response?.data?.message || err?.message || 'Submit failed');
        } finally {
          setQtSubmitLoading(false);
        }
        break;
      case 'send':
        setSendConfirmOpen(true);
        break;
      case 'accept':
        setAcceptConfirmOpen(true);
        break;
      case 'revise':
        setReviseOpen(true);
        break;
      case 'create_contract_review':
        createContractReviewMutation.mutate();
        break;
      case 'open_contract_review':
        if (contractReviews[0]) navigate(`/growth/contract-reviews/${contractReviews[0].id}`);
        break;
      case 'convert_so':
        await handleConvertToSalesOrder();
        break;
      case 'view_so':
        if (quotation.sales_order_id) navigate(`/growth/orders/${quotation.sales_order_id}`);
        break;
      case 'open_current':
        if (workspace.currentOffer) navigate(`/growth/quotations/${workspace.currentOffer.id}`);
        break;
      case 'edit':
        handleEdit();
        break;
      default:
        break;
    }
  };

  const { data: revenueServiceTypes = [] } = useQuery({
    queryKey: ['sales-revenue-service-types', 'active'],
    queryFn: async () => {
      const res = await api.get('/sales/revenue-service-types?only_active=true');
      const body = (res as any)?.data ?? res;
      const list = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      return list as { id: string; service_code: string; service_name: string }[];
    },
  });

  const quotationRstLabel = useMemo(() => {
    const rid = quotation?.revenue_service_type_id;
    if (!rid) return undefined;
    const r = revenueServiceTypes.find((x) => x.id === rid);
    return r ? `${r.service_code} - ${r.service_name}` : undefined;
  }, [quotation?.revenue_service_type_id, revenueServiceTypes]);

  const quotationRst = useMemo(() => {
    const rid = quotation?.revenue_service_type_id;
    if (!rid) return null;
    return revenueServiceTypes.find((x) => x.id === rid) ?? null;
  }, [quotation?.revenue_service_type_id, revenueServiceTypes]);

  /** When universal approval is used, the instance status is the source of truth (avoids Draft + Approved mismatch on the row). */
  const { data: approvalSummary } = useQuery({
    queryKey: ['approval-summary', ENTITY_TYPE_QUOTATION, id],
    queryFn: () => approvalService.getSummary(ENTITY_TYPE_QUOTATION, id!),
    enabled: !!id && !!quotation?.approval_instance_id,
  });
  const approvalInstance = approvalSummary?.instance;

  const approvalColorMap: Record<string, string> = {
    Pending: 'default',
    Approved: 'success',
    Rejected: 'error'
  };

  const approvalHeaderTag = useMemo(() => {
    if (!quotation) return { label: '', color: 'default' as const };
    if (approvalInstance) {
      const st = approvalInstance.status;
      if (st === 'pending_approval') return { label: 'Pending approval', color: 'processing' as const };
      if (st === 'approved') return { label: 'Approved', color: 'success' as const };
      if (st === 'rejected') return { label: 'Rejected', color: 'error' as const };
      if (st === 'cancelled') return { label: 'Cancelled', color: 'default' as const };
    }
    return {
      label: quotation.approval_status,
      color: (approvalColorMap[quotation.approval_status] ?? 'default') as
        | 'success'
        | 'processing'
        | 'error'
        | 'default'
        | 'warning',
    };
  }, [quotation, approvalInstance]);

  const canSendToCustomer = Boolean(
    quotation &&
      quotation.status === 'Draft' &&
      quotation.approval_status === 'Approved' &&
      (!quotation.approval_instance_id || approvalInstance?.status === 'approved')
  );
  const revenueServiceTypeLabel = useMemo(() => {
    if (!quotation?.revenue_service_type_id) return 'â€”';
    const r = revenueServiceTypes.find((x) => x.id === quotation.revenue_service_type_id);
    return r ? `${r.service_code} - ${r.service_name}` : '-';
  }, [quotation?.revenue_service_type_id, revenueServiceTypes]);

  // Helper function to safely convert to number
  const toNumber = (value: any): number => {
    if (value === null || value === undefined) return 0;
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const displayCurrency = quotation?.currency || 'AED';

  const itemColumns: ColumnsType<QuotationItem> = useMemo(() => {
    const cols: ColumnsType<QuotationItem> = [
      {
        title: '#',
        dataIndex: 'line_number',
        key: 'line_number',
        width: 60,
        align: 'center',
      },
    ];
    if (showRentalLineColumns) {
      cols.push({
        title: 'Type',
        dataIndex: 'line_type',
        key: 'line_type',
        width: 90,
        render: (v: string) => (
          <Tag color={String(v || '').toLowerCase() === 'rental' ? 'purple' : 'blue'}>
            {v || 'Trading'}
          </Tag>
        ),
      });
    }
    cols.push(
      {
        title: 'Item Code',
        dataIndex: 'item_code',
        key: 'item_code',
        width: 120,
      },
      {
        title: 'Revision',
        dataIndex: 'revision',
        key: 'revision',
        width: 80,
        render: (v: string) => v || 'â€”',
      },
      {
        title: 'Description',
        dataIndex: 'item_description',
        key: 'item_description',
        width: 280,
        render: (val: string, record: QuotationItem) => (
          <div>
            <div>{val}</div>
            {isRentalQuotationItem(record) && (
              <>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {formatRentalPeriod(record)}
                  </Text>
                </div>
                {record.specifications ? (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {record.specifications}
                    </Text>
                  </div>
                ) : null}
                {formatRentalCharges(record) ? (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatRentalCharges(record)}
                    </Text>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ),
      }
    );
    if (showRentalLineColumns) {
      cols.push({
        title: 'Qty / Duration',
        key: 'rental_qty_duration',
        width: 140,
        align: 'right',
        render: (_: unknown, record: QuotationItem) =>
          isRentalQuotationItem(record)
            ? formatRentalQuantity(record)
            : `${record.quantity} ${record.uom || ''}`,
      });
      cols.push({
        title: 'Period',
        dataIndex: 'rental_period_type',
        key: 'rental_period_type',
        width: 90,
        render: (v: string, record: QuotationItem) =>
          isRentalQuotationItem(record) ? v || 'â€”' : 'â€”',
      });
    } else {
      cols.push({
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        width: 100,
        align: 'right',
        render: (val: number, record: QuotationItem) => `${val} ${record.uom || ''}`,
      });
    }
    cols.push({
      title: showRentalLineColumns ? 'Unit price / Rate' : 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 130,
      align: 'right',
      render: (val: number, record: QuotationItem) => {
        const label = isRentalQuotationItem(record) ? 'Rate' : 'Unit';
        const price = isRentalQuotationItem(record) ? rentalLineRate(record) : toNumber(val);
        return (
          <span>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {label}{' '}
            </Text>
            {displayCurrency} {price.toFixed(2)}
          </span>
        );
      },
    });
    cols.push(
      {
        title: 'Discount',
        dataIndex: 'discount_percentage',
        key: 'discount_percentage',
        width: 100,
        align: 'right',
        render: (val: number, record: QuotationItem) =>
          toNumber(val) > 0
            ? `${toNumber(val)}% (${displayCurrency} ${toNumber(record.discount_amount).toFixed(2)})`
            : '-',
      },
      {
        title: 'Net Amount',
        dataIndex: 'net_amount',
        key: 'net_amount',
        width: 120,
        align: 'right',
        render: (val: number, record: QuotationItem) => (
          <div>
            <Text strong>
              {displayCurrency} {toNumber(val).toFixed(2)}
            </Text>
            {record.vat_applicable && toNumber(record.vat_amount) > 0 ? (
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  + VAT {displayCurrency} {toNumber(record.vat_amount).toFixed(2)}
                </Text>
              </div>
            ) : null}
          </div>
        ),
      }
    );
    return cols;
  }, [showRentalLineColumns, displayCurrency]);

  const handleBack = () => {
    navigate('/growth/quotations');
  };

  const handleEdit = () => {
    navigate(`/growth/quotations/edit/${id}`);
  };

  const handleApprove = async () => {
    try {
      await approveQuotationMutation.mutateAsync(id!);
      message.success('Quotation approved successfully');
    } catch (error) {
      message.error('Failed to approve quotation');
    }
  };

  const handleSend = async () => {
    try {
      await sendQuotationMutation.mutateAsync(id!);
      message.success('Quotation sent successfully');
    } catch (error) {
      message.error('Failed to send quotation');
    }
  };

  const handleAccept = async () => {
    try {
      await acceptQuotationMutation.mutateAsync({ id: id! });
      message.success('Quotation marked as Accepted successfully');
    } catch (error) {
      message.error('Failed to accept quotation');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteQuotationMutation.mutateAsync(id!);
      message.success('Quotation deleted successfully');
      navigate('/growth/quotations');
    } catch (error) {
      message.error('Failed to delete quotation');
    }
  };

  const handleConvertToSalesOrder = async (departmentIdOverride?: string) => {
    try {
      // Check quotation status before attempting conversion
      if (quotation && quotation.status !== 'Accepted') {
        message.error(`Cannot convert quotation. Status must be "Accepted", current status: "${quotation.status}"`);
        return;
      }
      
      if (quotation && quotation.is_converted_to_sales_order) {
        message.warning('This quotation has already been converted to a customer order');
        return;
      }

      if (!canConvertWithContractReview) {
        message.error(
          'Contract Review must be approved before Customer Order conversion. Create and complete an approved Contract Review first.'
        );
        return;
      }

      const existingDept =
        departmentIdOverride?.trim() ||
        (quotation?.department_id ? String(quotation.department_id).trim() : '');
      if (!existingDept) {
        setConvertDepartmentId(undefined);
        setConvertDeptModalOpen(true);
        return;
      }

      const result = await convertToSalesOrderMutation.mutateAsync({
        quotationId: id!,
        departmentId: existingDept,
      });
      setConvertDeptModalOpen(false);
      message.success(`Quotation converted to Customer Order ${result.sales_order_id ? `#${result.sales_order_id}` : ''} successfully`);
      // Navigate to sales orders list page to see the new order
      // Small delay to ensure backend has processed the transaction
      setTimeout(() => {
        navigate('/growth/orders');
      }, 500);
    } catch (error: any) {
      // Error is already handled in the mutation's onError
      console.error('Error in handleConvertToSalesOrder:', error);
      // Don't show duplicate error message as mutation already handles it
    }
  };

  const runPrint = async (mode: QuotationPrintMode, overheadOption: CustomerOverheadPrintOption) => {
    if (!quotation) return;

    console.log('ðŸ“ Printing quotation with items:', quotation.items?.length);
    console.log('Items:', quotation.items?.map(i => ({ line: i.line_number, code: i.item_code })));

    let approvalSigners: Awaited<ReturnType<typeof approvalService.getPrintSigners>> = [];
    try {
      approvalSigners = await approvalService.getPrintSigners('quotation', quotation.id);
    } catch (err) {
      console.warn('Quotation print signers unavailable', err);
      approvalSigners = [];
    }

    // Extract company info from settings
    const companySettings = settings?.filter(s => s.category === 'company') || [];
    const getSettingValue = (key: string) => {
      const setting = companySettings.find(s => s.setting_key === key);
      return setting?.setting_value || '';
    };

    const companyInfo = {
      name: getSettingValue('company_name') || 'Your Company Name',
      address: getSettingValue('company_address') || '123 Business Street, City, Country',
      phone: getSettingValue('company_phone') || '+971 XX XXX XXXX',
      email: getSettingValue('company_email') || 'info@company.com',
      website: getSettingValue('company_website') || '',
      taxNumber: getSettingValue('tax_registration_number') ? `TRN: ${getSettingValue('tax_registration_number')}` : '',
      tradeNumber: getSettingValue('trade_license_number') || '',
      vatNumber: getSettingValue('vat_registration_number') || '',
      logo: getSettingValue('company_logo') || undefined
    };

    // Hide the main app content
    const mainContent = document.getElementById('root');
    if (mainContent) {
      mainContent.style.display = 'none';
    }

    // Create a print container
    const printContainer = document.createElement('div');
    printContainer.id = 'print-container';
    printContainer.style.display = 'block';
    document.body.appendChild(printContainer);

    // Force letterhead page margins before paint/print (named @page is unreliable in Chrome).
    const uninstallPageMargins = installSalesQuotationPrintPageMargins();

    // Cleanup function
    const cleanup = () => {
      uninstallPageMargins();
      root.unmount();
      document.body.removeChild(printContainer);
      if (mainContent) {
        mainContent.style.display = 'block';
      }
      window.removeEventListener('afterprint', cleanup);
    };

    // Render the printable component with close handler and company info
    const root = ReactDOM.createRoot(printContainer);
    root.render(
      <PrintableQuotation
        quotation={quotation as any}
        revenueServiceTypeLabel={quotationRstLabel}
        revenueServiceType={quotationRst}
        printMode={mode}
        customerOverheadDisplay={overheadOption}
        companyInfo={companyInfo}
        approvalSigners={approvalSigners}
        coverLetter={printCoverLetter}
        onClose={cleanup}
      />
    );

    // Listen for print completion
    window.addEventListener('afterprint', cleanup);
  };

  const handlePrint = () => {
    // Always offer Customer / Technical (+ rental & internal when available).
    setPrintModalOpen(true);
  };

  const printCustomerPdf = async () => {
    if (!quotation) return;
    const hide = message.loading('Preparing quotation PDF...', 0);
    try {
      openBlobInNewTab(await getReportDocumentFile('sales-quotation', 'pdf', { id: quotation.id }));
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || 'Could not generate the quotation PDF');
    } finally {
      hide();
    }
  };

  const confirmPrint = () => {
    setPrintModalOpen(false);
    const overhead =
      printMode === 'customer' || printMode === 'customer_rental_compact'
        ? customerOverheadDisplay
        : 'none';
    void runPrint(printMode, overhead);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
      {id ? <AiPageContextSync module="sales" docType="Quotation" docId={id} /> : null}
        <Spin size="large" tip="Loading quotation..." />
      </div>
    );
  }

  if (!quotation) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <Text>Quotation not found</Text>
          <Button onClick={handleBack} style={{ marginLeft: 16 }}>
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  const showUniversalApproval = !!quotation.approval_instance_id;

  const statusColorMap: Record<string, string> = {
    Draft: 'default',
    Sent: 'processing',
    Accepted: 'success',
    Rejected: 'error',
    Revised: 'warning'
  };

  const contractReviewOverview = (
    <Card size="small" title="Contract Review" style={{ marginTop: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        {isTradingRental ? (
          <Text type="secondary">
            Contract Review is not required for Trading & Rental quotations.
          </Text>
        ) : (
          <>
            {staleContractReview ? (
              <Text type="warning">
                {staleContractReview.review_number} is stale — create a new Contract Review before SO conversion.
              </Text>
            ) : null}
            {contractReviews.length === 0 ? (
              <Text>No contract reviews linked.</Text>
            ) : (
              <Space wrap>
                {contractReviews.map((cr) => (
                  <Tag key={cr.id} color={cr.review_status === 'Approved' ? 'green' : 'default'}>
                    {cr.review_number}: {cr.review_status}
                    {cr.is_stale ? ' (stale)' : ''}
                  </Tag>
                ))}
              </Space>
            )}
          </>
        )}
      </Space>
    </Card>
  );

  return (
    <div style={{ padding: '24px' }}>
      {id ? (
        <AiPageContextSync
          module="sales"
          docType="Quotation"
          docId={id}
          filters={{
            quotation_number: quotation.quotation_number,
            revision_number: quotation.revision_number,
            display_revision: displayQuotationRevisionLabel(quotation.revision_number),
            is_current_submitted: quotation.is_current_submitted,
            is_latest_revision: quotation.is_latest_revision,
          }}
        />
      ) : null}

      {sourceSync && (sourceSyncCommercial > 0 || sourceSyncHousekeeping > 0) ? (
        <Alert
          style={{ marginBottom: 16 }}
          showIcon
          type={sourceSyncCommercial > 0 ? 'warning' : 'info'}
          message={
            sourceSyncCommercial > 0
              ? `Estimation ${sourceSync.source.number} has changed since this quotation was prepared`
              : `Cost / link data from ${sourceSync.source.number} is out of date on this quotation`
          }
          description={
            sourceSyncCommercial > 0
              ? [
                  sourceSync.summary.added ? `${sourceSync.summary.added} new line(s)` : null,
                  sourceSync.summary.removed ? `${sourceSync.summary.removed} removed` : null,
                  sourceSync.summary.quantity +
                  sourceSync.summary.price +
                  sourceSync.summary.description +
                  sourceSync.summary.specification
                    ? `${
                        sourceSync.summary.quantity +
                        sourceSync.summary.price +
                        sourceSync.summary.description +
                        sourceSync.summary.specification
                      } changed`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ') +
                (sourceSync.eligibility.needs_revision ? ' — resyncing will create a new revision.' : '')
              : 'Resync to refresh line costs and margins from the estimation. Selling prices are not changed.'
          }
          action={
            <Button
              size="small"
              type={sourceSyncCommercial > 0 ? 'primary' : 'default'}
              onClick={() => navigate(`/growth/quotations/${quotation.id}/source-sync`)}
            >
              Review &amp; resync
            </Button>
          }
        />
      ) : null}

      <QuotationNegotiationWorkspace
        quotation={quotation}
        family={workspace.family}
        familyLoading={workspace.familyLoading}
        comparison={workspace.comparison}
        compareLoading={workspace.compareLoading}
        currentOffer={workspace.currentOffer}
        contractReviews={contractReviews}
        estimateLabel={estimateLabel}
        onOpenEstimate={hasSourceEstimation ? openEstimate : undefined}
        currentEstimationRevision={linkedEstimation?.revision_number ?? null}
        latestApprovedEstimateId={latestApprovedEstimate?.id ?? null}
        latestApprovedEstimateRevision={latestApprovedEstimate?.revision_number ?? null}
        estimateCostDelta={estimateCostDelta}
        onOpenEstimateReconciliation={(targetId) =>
          navigate(
            `/growth/quotations/${quotation.id}/estimate-reconciliation?targetEstimationId=${targetId}`
          )
        }
        revenueServiceTypeLabel={revenueServiceTypeLabel}
        itemColumns={itemColumns}
        showUniversalApproval={showUniversalApproval}
        universalApprovalPending={approvalInstance?.status === 'pending_approval'}
        universalApprovalApproved={approvalInstance?.status === 'approved'}
        approvalSummary={approvalSummary}
        isTradingRental={isTradingRental}
        canViewProfitability={canViewProfitability}
        canEdit={quotationEditCheck.ok}
        canRevise={quotationReviseCheck.ok}
        canDelete={quotationDeleteCheck.ok}
        linkedSalesOrders={linkedSalesOrders as Array<Record<string, unknown>>}
        salesOrderNumber={linkedSalesOrder?.sales_order_number ?? null}
        overviewExtra={contractReviewOverview}
        sendConfirmOpen={sendConfirmOpen}
        acceptConfirmOpen={acceptConfirmOpen}
        priorOfferRevision={priorOfferRevision}
        sendLoading={sendQuotationMutation.isPending}
        acceptLoading={acceptQuotationMutation.isPending}
        onSendCancel={() => setSendConfirmOpen(false)}
        onSendConfirm={async () => {
          await handleSend();
          setSendConfirmOpen(false);
        }}
        onAcceptCancel={() => setAcceptConfirmOpen(false)}
        onAcceptConfirm={async () => {
          await handleAccept();
          setAcceptConfirmOpen(false);
        }}
        actions={{
          onBack: handleBack,
          onPrimaryCta: handlePrimaryCta,
          onEdit: handleEdit,
          onPrint: handlePrint,
          onTransfer: () => setTransferOpen(true),
          onRevise: () => setReviseOpen(true),
          primaryCtaLoading:
            qtSubmitLoading ||
            sendQuotationMutation.isPending ||
            acceptQuotationMutation.isPending ||
            convertToSalesOrderMutation.isPending ||
            createContractReviewMutation.isPending,
        }}
        onApproveViaDocument={async (remarks) => {
          await quotationService.approve(quotation.id, remarks);
          queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(quotation.id) });
          queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
        }}
        onRejectViaDocument={async (payload) => {
          await quotationService.rejectUniversalApproval(quotation.id, payload as any);
          queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.detail(quotation.id) });
          queryClient.invalidateQueries({ queryKey: QUOTATION_KEYS.lists() });
        }}
      />

      <SalesTeamTransferModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        entityType="Quotation"
        entityId={quotation.id}
        entityLabel={quotation.quotation_number}
        currentTeamName={quotation.sales_team_name}
        onSuccess={() => queryClient.invalidateQueries()}
      />
      <CreateQuotationRevisionModal
        open={reviseOpen}
        quotation={quotation}
        loading={createRevisionMutation.isPending}
        onCancel={() => setReviseOpen(false)}
        onSubmit={async (values) => {
          const res = await createRevisionMutation.mutateAsync({
            id: quotation.id,
            revisionReason: values.revision_reason,
            revisedData: {
              revision_type: values.revision_type,
              revision_notes: values.revision_notes,
              estimate_source: values.estimate_source,
              selected_estimation_id: values.selected_estimation_id,
            },
          });
          setReviseOpen(false);
          const newId =
            (res as { data?: { id?: string }; id?: string })?.data?.id ||
            (res as { id?: string })?.id;
          if (newId) navigate(`/growth/quotations/${newId}`);
        }}
      />
      <TransferHistoryPanel entityType="Quotation" entityId={quotation.id} />

      <Modal
        title={`Print ${formatQuotationPrintIdentity(quotation.quotation_number, quotation.revision_number)}`}
        open={printModalOpen}
        onCancel={() => setPrintModalOpen(false)}
        onOk={confirmPrint}
        okText="Print"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text strong>Print mode</Text>
            <Radio.Group
              style={{ display: 'block', marginTop: 8 }}
              value={printMode}
              onChange={(e) => setPrintMode(e.target.value)}
            >
              <Radio value="customer">Customer (no cost/margin)</Radio>
              <Radio value="technical">Technical / Operations (no pricing)</Radio>
              {showRentalLineColumns ? (
                <Radio value="customer_rental_compact">
                  Customer rental (no dates; description without qty/period)
                </Radio>
              ) : null}
              {canViewProfitability ? (
                <>
                  <Radio value="internal">Internal (cost & margin)</Radio>
                  <Radio value="internal_breakdown">Internal with overhead breakdown</Radio>
                </>
              ) : null}
            </Radio.Group>
          </div>
          {printMode === 'customer' || printMode === 'customer_rental_compact' ? (
            <div>
              <Text strong>Customer overhead display</Text>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={customerOverheadDisplay}
                onChange={setCustomerOverheadDisplay}
                options={[
                  { value: 'none', label: 'Do not show overheads' },
                  { value: 'summary', label: 'Show commercial charges summary' },
                  { value: 'breakdown', label: 'Show detailed overhead breakdown' },
                ]}
              />
            </div>
          ) : null}
        </Space>
      </Modal>
      <Modal
        title="Department required for Customer Order"
        open={convertDeptModalOpen}
        onCancel={() => setConvertDeptModalOpen(false)}
        confirmLoading={convertToSalesOrderMutation.isPending}
        onOk={() => {
          if (!convertDepartmentId?.trim()) {
            message.warning('Department is required');
            return;
          }
          return handleConvertToSalesOrder(convertDepartmentId.trim());
        }}
        okText="Convert to Customer Order"
      >
        <DepartmentSelect
          value={convertDepartmentId}
          onChange={(value) => setConvertDepartmentId(value || undefined)}
          style={{ width: '100%' }}
          placeholder="Select department"
        />
      </Modal>
    </div>
  );
};

export default QuotationDetailPage;
